import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import AsyncSelect from 'react-select/async';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import AutoCompleteInput from 'components/Common/Inputs/AutoCompleteInput';
import HintButton from 'components/Common/Buttons/HintButton';

import { ApiService } from 'services';

export const CustomerAccountInputLayout = {
  Default: 0,
  Horizontal: 1
};

function CustomerAccountInput(props) {
  const emptySelections = {
    customerId: null,
    accountId: null,
    accounts: [],
    newCustomer: false,
    newAccount: false,
    legalEntities: [],
    selectionValid: false,
    errors: []
  };

  const { clientId, onSelectionChange, layout, initialSelections, accountExclusions, accountsPartOfAnImyStructure, tempCustomerLabel, permanentCustomer } = props;
  const permanentAvailable = !!(permanentCustomer && permanentCustomer.available);
  const permanentSelected = permanentAvailable && !!permanentCustomer.selected;

  if (initialSelections && initialSelections.customer) {
    initialSelections.customerId = initialSelections.customer.id;
  }

  const [customer, setCustomer] = useState(initialSelections
    && initialSelections.customer
    && Object.assign(initialSelections.customer, {
      value: `${ initialSelections.customer.customerId } - ${ initialSelections.customer.name }`,
      type: 'customeraccount'
    }));
  const [legalEntities, setLegalEntities] = useState([]);
  const [legalEntityOptions, setLegalEntityOptions] = useState([]);
  const [legalEntityInputValue, setLegalEntityInputValue] = useState('');
  const [excluded, setExcluded] = useState([]);
  const [selections, setSelections] = useState(Object.assign(emptySelections, initialSelections ?? {}));
  const [leChapter3Shown, setLeChapter3Shown] = useState(false);
  const [leChapter4Shown, setLeChapter4Shown] = useState(false);
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [customerAccountOptions, setCustomerAccountOptions] = useState([]);
  const [customerAccountInputValue, setCustomerAccountInputValue] = useState('');
  const [searchTimer, setSearchTimer] = useState();
  const [soleLegalEntity, setSoleLegalEntity] = useState(null);
  const searchTimerDelay = 500;

  useEffect(() => {
    setSelections(emptySelections);
    setCustomer(null);
    setLegalEntities([]);
    setLegalEntityOptions([]);
    setLegalEntityInputValue('');
    setExcluded([]);
    setLeChapter3Shown(false);
    setLeChapter4Shown(false);
    setCustomerAccounts([]);
    setCustomerAccountOptions([]);
    setCustomerAccountInputValue('');
  }, [clientId]);

  useEffect(() => {
    setSoleLegalEntity(null);
    ApiService
      .getDefaultLegalEntity(clientId)
      .then((response) => {
        const { legalEntity, count } = response.data;
        setSoleLegalEntity(count === 1 && legalEntity ? { ...legalEntity, label: legalEntity.value, value: legalEntity.id } : null);
      })
      .catch(() => setSoleLegalEntity(null));
  }, [clientId]);

  const fieldHasErrors = (field) => {
    if (permanentSelected) {
      return false;
    }
    if (selections.errors && selections.errors.length && selections.errors.filter(error => error.type === field).length > 0) {
      return true;
    }
    return false;
  };

  const validate = (data) => {
    const errors = [];

    if (data.customerId === null && !data.newCustomer) {
      errors.push({ type: 'customer', message: 'no customer selected' });
    }

    if ((!data.newCustomer) && !data.newAccount && data.accounts.length === 0) {
      errors.push({ type: 'account', message: 'no account(s) selected' });
    }

    if ((data.newCustomer || data.newAccount) && data.legalEntities.length === 0) {
      errors.push({ type: 'legalEntities', message: 'no legal entity(ies) selected' });
    }

    return errors;
  };

  const isHorizonalLayout = () => layout === CustomerAccountInputLayout.Horizontal;

  const removeDuplicates = (arr, equals) => {
    const originalArr = arr.slice(0);
    arr.length = 0;

    for (let i = 0; i < originalArr.length; i += 1) {
      const val = originalArr[i];
      if (!arr.some(item => equals(item, val))) {
        arr.push(val);
      }
    }
    return arr;
  };

  const convertLegalEntity = le => Object.assign(le, { value: le.id, label: `${ le.identifier } - ${ le.name }` });

  const convertCustomerAccount = selectedAccount => Object.assign(
    {
      value: selectedAccount.id,
      label: selectedAccount.value,
      parentAccountId: selectedAccount.parentAccountId,
      withholdingStatementId: selectedAccount.withholdingStatementId
    }
  );

  const updateCustomerAccount = (type, value) => {
    let newCustomerAccountObj = {};
    newCustomerAccountObj = Object.assign({}, newCustomerAccountObj, selections);

    if (type === 'customer') {
      setCustomer(value);
      if (value === null) {
        newCustomerAccountObj.newAccount = false;
        newCustomerAccountObj.accountId = null;
        newCustomerAccountObj.accounts = [];
        newCustomerAccountObj.legalEntities = [];
        setCustomerAccounts([]);
        setLegalEntities([]);
        setSelections(newCustomerAccountObj);
        onSelectionChange(newCustomerAccountObj);
        return;
      }
      newCustomerAccountObj.customerId = value.id;
      const filteredAccounts = value?.accounts?.filter(a => !accountExclusions || !accountExclusions.includes(a.id));
      if (filteredAccounts) {
        setCustomerAccountOptions(filteredAccounts);
      }
      if (!filteredAccounts || filteredAccounts.length < 1) {
        newCustomerAccountObj.newAccount = true;
      } else if (filteredAccounts.length === 1) {
        const selectedAccount = value.accounts[0];
        if (accountExclusions && accountExclusions.includes(selectedAccount.id)) {
          newCustomerAccountObj.newAccount = true;
        } else {
          newCustomerAccountObj.newAccount = false;
          newCustomerAccountObj.accountId = selectedAccount.id;
          newCustomerAccountObj.accounts = [convertCustomerAccount(selectedAccount)];
          newCustomerAccountObj.legalEntities = [convertLegalEntity(selectedAccount.legalEntity)];
          setCustomerAccounts([convertCustomerAccount(selectedAccount)]);
          setLegalEntities([convertLegalEntity(selectedAccount.legalEntity)]);
        }
      }
    }

    if (type === 'newcustomer') {
      newCustomerAccountObj.newCustomer = value;
      newCustomerAccountObj.legalEntities = [];
      newCustomerAccountObj.newAccount = value;
      newCustomerAccountObj.accountId = null;
      newCustomerAccountObj.accounts = [];
      setCustomer(null);
      setLegalEntities([]);
      setCustomerAccounts([]);
      setExcluded([]);
    }

    if (type === 'account') {
      newCustomerAccountObj.accounts = value;
      setCustomerAccounts(value);

      if (customer !== null && value !== '' && value.length > 0) {
        const accountIds = value.map(v => Number(v.value));
        const accounts = customer?.accounts?.filter(a => accountIds.includes(a.id));
        if (accounts) {
          const uniqueIds = accounts
            .map(a => a.legalEntity.id)
            .filter((val, index, self) => self.indexOf(val) === index);
          const LEs = accounts
            .filter(a => uniqueIds.includes(a.legalEntity.id))
            .map(a => ({ ...a.legalEntity, value: a.legalEntity.id, label: `${ a.legalEntity.identifier } - ${ a.legalEntity.name }` }));
          newCustomerAccountObj.legalEntities = removeDuplicates(LEs, (left, right) => left.id === right.id);
          setLegalEntities(LEs);
          const selectedCustomerAccounts = accounts.map(a => convertCustomerAccount(a));
          newCustomerAccountObj.accounts = selectedCustomerAccounts;
          setCustomerAccounts(selectedCustomerAccounts);
        }
      } else {
        setCustomerAccounts([]);
        setLegalEntities([]);
      }
    }

    if (type === 'newaccount') {
      if (newCustomerAccountObj.newCustomer) {
        return;
      }
      setLegalEntities([]);
      setCustomerAccounts([]);
      newCustomerAccountObj.accountId = null;
      newCustomerAccountObj.accounts = [];
      newCustomerAccountObj.legalEntities = [];
      newCustomerAccountObj.newAccount = value;

      if (customer !== null && !newCustomerAccountObj.newCustomer && newCustomerAccountObj.newAccount) {
        const excludedLegalEntites = [];
        if (customer.accounts) {
          customer.accounts.map((item) => {
            setExcluded(excludedLegalEntites.push(`${ item.legalEntity.identifier } - ${ item.legalEntity.name }`));
            return item;
          });
        }
        setExcluded(excludedLegalEntites);
      }

      if (!newCustomerAccountObj.newAccount) {
        if (customer && customer.accounts) {
          const filteredAccounts = customer.accounts.filter(a => !accountExclusions || !accountExclusions.includes(a.id));
          if (filteredAccounts.length < 1) {
            newCustomerAccountObj.newAccount = true;
          } else if (filteredAccounts.length === 1) {
            const selectedAccount = customer.accounts[0];
            if (accountExclusions && accountExclusions.includes(selectedAccount.id)) {
              newCustomerAccountObj.newAccount = true;
            } else {
              newCustomerAccountObj.accountId = selectedAccount.id;
              newCustomerAccountObj.accounts = [convertCustomerAccount(selectedAccount)];
              newCustomerAccountObj.legalEntities = [convertLegalEntity(selectedAccount.legalEntity)];
              setLegalEntities([convertLegalEntity(selectedAccount.legalEntity)]);
              setCustomerAccounts([convertCustomerAccount(selectedAccount)]);
            }
          }
        }
        setExcluded([]);
      }
    }

    if (type === 'legalentity') {
      setLegalEntities(value);
      newCustomerAccountObj.legalEntities = value;
    }

    newCustomerAccountObj.errors = validate(newCustomerAccountObj);
    newCustomerAccountObj = Object.assign({}, selections, newCustomerAccountObj);
    newCustomerAccountObj.selectionValid = newCustomerAccountObj.errors.length === 0;
    setSelections(newCustomerAccountObj);
    onSelectionChange(newCustomerAccountObj);
  };

  useEffect(() => {
    if (soleLegalEntity && permanentSelected && (permanentCustomer.legalEntities || []).length === 0) {
      permanentCustomer.onLegalEntityChange([soleLegalEntity]);
    }
  }, [soleLegalEntity, permanentSelected]);

  useEffect(() => {
    if (soleLegalEntity && !permanentSelected && (selections.newCustomer || selections.newAccount) && legalEntities.length === 0) {
      updateCustomerAccount('legalentity', [soleLegalEntity]);
    }
  }, [soleLegalEntity, permanentSelected, selections.newCustomer, selections.newAccount]);

  const onCustomerChange = (e) => {
    if ((e.target.value.length > 0 && customer !== null && e.target.value !== customer.value) || e.target.value.length === 0) {
      updateCustomerAccount('customer', null);
    }
  };

  const getAccountOptionsFromCustomer = () => {
    if (selections.newCustomer
      || selections.newAccount
      || (customer !== null && customer.accounts && customer.accounts.length && customer.accounts.length < 1)) {
      return [];
    }
    if (customer && customer.accounts) {
      const filteredAccounts = accountExclusions ? customer.accounts.filter(a => !accountExclusions.includes(a.id)) : customer.accounts;

      return filteredAccounts.map(account => ({ value: account.id, label: account.value }));
    }
    return [];
  };

  const loadLegalEntities = (inputValue, callback) => {
    if (!inputValue || inputValue.length < 3) {
      setLegalEntityOptions([]);
      callback([]);
    } else {
      const postData = {
        type: 'legalentity',
        activeOnly: true,
        clientContext: { type: 'client', id: parseInt(clientId, 10), value: '' },
        valueEntered: inputValue,
      };

      ApiService
        .lookup(postData)
        .then((result) => {
          const leValues = result.data
            .filter(data => !excluded.includes(data.value))
            .map(l => ({ ...l, label: l.value, value: l.id }));
          setLegalEntityOptions(leValues);
          callback(leValues);
        });
    }
  };

  const loadCustomerAccounts = (inputValue, callback) => {
    if (!inputValue || inputValue.length < 1) {
      setCustomerAccountOptions([]);
      callback([]);
    } else {
      clearTimeout(searchTimer);
      setSearchTimer(setTimeout(() => {
        const postData = {
          type: 'account',
          activeOnly: true,
          clientContext: { type: 'client', id: parseInt(clientId, 10), value: '' },
          context: { type: 'customer', id: parseInt(customer.id, 10), value: '' },
          valueEntered: inputValue,
        };

        ApiService
          .lookup(postData)
          .then((result) => {
            const accounts = result.data
              .filter(data => !excluded.includes(data.value))
              .map(account => ({ ...account, label: account.value, value: account.id }));
            setCustomerAccountOptions(accounts);
            callback(accounts);
          });
      }, searchTimerDelay));
    }
  };

  const onCustomerAccountInputChange = (inputValue, { action }) => {
    if (!['input-blur', 'menu-close'].includes(action)) {
      setCustomerAccountInputValue(inputValue);
    }
  };

  const onLegalEntityInputChange = (inputValue, { action }) => {
    if (!['input-blur', 'menu-close'].includes(action)) {
      setLegalEntityInputValue(inputValue);
    }
  };

  const onMultiLegalEntityChange = (value) => {
    updateCustomerAccount('legalentity', value);
  };

  const onMultiCustomerAccountChange = (value) => {
    updateCustomerAccount('account', value);
  };

  const getTopLevelImyAccountId = account => account.parentAccountId ?? account.value;
  const horizLayout = isHorizonalLayout();
  const newCustomerMode = selections.newCustomer || permanentSelected;
  const inputCols = (horizLayout && '8') || (permanentAvailable ? '3' : '4');
  const optionCols = (horizLayout && '8') || (permanentAvailable ? '4' : '3');

  const onTempCustomerChange = (checked) => {
    if (checked && permanentSelected) {
      permanentCustomer.onToggle(false);
    }
    updateCustomerAccount('newcustomer', checked);
  };

  const onPermanentCustomerChange = (checked) => {
    if (checked) {
      setCustomer(null);
      setCustomerAccounts([]);
      setLegalEntities([]);
      setExcluded([]);
      setSelections({ ...emptySelections });
    }
    permanentCustomer.onToggle(checked);
  };

  return (
    <React.Fragment>
      <div className={ `form-group ${ horizLayout ? 'col-4' : '' } col-sm-12` }>
        <div className="col-4 col-sm-12">
          <label htmlFor={ permanentSelected ? 'permanent-customer-id' : 'customer' } className="form-label text-right">Customer *</label>
        </div>

        <div className={ `col-${ inputCols } col-sm-12` }>
          { permanentSelected && <React.Fragment>
            <input
              id="permanent-customer-id"
              name="permanentCustomerId"
              type="text"
              className={ `form-input ${ permanentCustomer.customerIdError ? 'is-error' : '' }` }
              placeholder="Enter Customer ID..."
              autoComplete="off"
              required
              value={ permanentCustomer.customerId }
              disabled={ permanentCustomer.disabled }
              onChange={ e => permanentCustomer.onCustomerIdChange(e.target.value) }
            />
            { permanentCustomer.customerIdError && <p id="permanent-customer-id-error" className="form-input-hint is-error">{ permanentCustomer.customerIdError }</p> }
          </React.Fragment> }
          { !permanentSelected && <AutoCompleteInput
            id="customer-id"
            name="customerId"
            category="customeraccount"
            clientContext={ { type: 'client', id: parseInt(clientId, 10), value: '' } }
            context={ null }
            placeholder="Enter Customer ID..."
            className={ fieldHasErrors('customer') ? 'is-error' : '' }
            required
            value={ !selections.newCustomer && customer !== null ? customer : '' }
            disabled={ selections.newCustomer }
            onChange={ e => onCustomerChange(e) }
            onSelectedItem={ e => updateCustomerAccount('customer', e) }
          /> }
        </div>
        <AccessControl matchAll allowedPermissions={ [ActivityPerms.customerEdit, ActivityPerms.accountEdit] }>
          <div className={ `col-${ isHorizonalLayout() ? '4' : '1' } col-sm-12` }>
            <label className="form-label text-right">OR</label>
          </div>
          <div className={ `col-${ optionCols } col-sm-12 text-left create-customer-options` }>
            <label className="form-checkbox">
              <input
                type="checkbox"
                id="create-customer"
                name="createCustomer"
                value="true"
                className={ fieldHasErrors('customer') ? 'is-error' : '' }
                onChange={ e => onTempCustomerChange(e.target.checked) }
                checked={ selections.newCustomer }
                disabled={ permanentSelected && permanentCustomer.disabled }
              />
              <i className={ `form-icon ${ fieldHasErrors('customer') && 'is-error' }` } />
              { tempCustomerLabel }
            </label>
            { permanentAvailable && <label className="form-checkbox">
              <input
                type="checkbox"
                id="create-permanent-customer"
                name="createPermanentCustomer"
                value="true"
                onChange={ e => onPermanentCustomerChange(e.target.checked) }
                checked={ permanentSelected }
                disabled={ permanentSelected && permanentCustomer.disabled }
              />
              <i id="create-permanent-customer-icon" className="form-icon" />
              Create New Customer
            </label> }
          </div>
        </AccessControl>
      </div>
      <div className={ `form-group ${ isHorizonalLayout() ? 'col-4' : '' } col-sm-12` } style={ (customer !== null || newCustomerMode) ? null : { display: 'none' } }>
        <div className="col-4 col-sm-12">
          <label htmlFor="customer-account-select" className="form-label text-right">Account *</label>
        </div>
        <div className={ `col-${ inputCols } col-sm-12` }>
          <AsyncSelect
            className={ fieldHasErrors('account') ? 'is-error' : '' }
            classNamePrefix="ttms"
            isMulti
            closeOnSelect={ false }
            closeMenuOnSelect={ false }
            cacheOptions
            placeholder={ newCustomerMode || selections.newAccount ? 'Auto-Generated Account' : 'Choose an Account' }
            loadOptions={ loadCustomerAccounts }
            defaultOptions={ getAccountOptionsFromCustomer() }
            isDisabled={ newCustomerMode || selections.newAccount || (customer?.accounts && customer.accounts.length === 1) }
            noOptionsMessage={ () => ((customerAccountInputValue && customerAccountOptions && customerAccountOptions.length === 0) ? 'No accounts found...' : 'Enter a customer identifier') }
            value={ customerAccounts }
            inputValue={ customerAccountInputValue }
            onInputChange={ onCustomerAccountInputChange }
            onChange={ onMultiCustomerAccountChange }
          />
        </div>
        <AccessControl allowedPermissions={ [ActivityPerms.accountEdit] }>
          <div className={ `col-${ isHorizonalLayout() ? '4' : '1' } col-sm-12` }>
            <label className="form-label text-right">OR</label>
          </div>
          <div className={ `col-${ optionCols } col-sm-12 text-left` }>
            <label className="form-checkbox">
              <input
                type="checkbox"
                id="create-account"
                name="createAccount"
                value="true"
                className={ fieldHasErrors('newaccount') ? 'is-error' : '' }
                onChange={ e => updateCustomerAccount('newaccount', e.target.checked) }
                checked={ selections.newAccount || permanentSelected }
                disabled={ permanentSelected }
              />
              <i className={ `form-icon ${ fieldHasErrors('newaccount') && 'is-error' }` } />
              Create New Account
            </label>
          </div>
        </AccessControl>
      </div>
      { accountsPartOfAnImyStructure
      && <div className={ `form-group ${ isHorizonalLayout() ? 'col-4' : '' } col-sm-12` }>
        <div className="offset-4 col-8 col-sm-12 error-container">
          <div className="error-text">
            {
              accountsPartOfAnImyStructure.map(a => <p>{ a.label } is linked to an IMY Structure. Any new form for this account must be uploaded from the <a href={ `/whd-statement/${ getTopLevelImyAccountId(a) }` }>IMY Structure page.</a></p>)
            }
          </div>
        </div>
      </div> }
      <div className={ `form-group ${ isHorizonalLayout() ? 'col-4' : '' } col-sm-12` }>
        { (legalEntities.length > 0 || selections.newCustomer || selections.newAccount || permanentSelected)
        && <div className="col-4 col-sm-12">
          <label htmlFor={ permanentSelected ? 'permanent-legal-entity-select' : 'customer-account-select' } className="form-label text-right">Legal Entity *</label>
        </div> }

        { permanentSelected
        && <div className={ `col-${ inputCols } col-sm-12` } id="permanent-legal-entity">
          <AsyncSelect
            inputId="permanent-legal-entity-select"
            classNamePrefix="ttms"
            isMulti
            closeOnSelect={ false }
            closeMenuOnSelect={ false }
            cacheOptions
            isClearable
            loadOptions={ loadLegalEntities }
            defaultOptions={ legalEntityOptions }
            isDisabled={ permanentCustomer.disabled || !!soleLegalEntity }
            noOptionsMessage={ () => ((legalEntityInputValue && legalEntityOptions && legalEntityOptions.length === 0) ? 'No legal entities found...' : 'Enter a legal entity identifier') }
            value={ permanentCustomer.legalEntities || [] }
            inputValue={ legalEntityInputValue }
            onInputChange={ onLegalEntityInputChange }
            onChange={ value => permanentCustomer.onLegalEntityChange(value) }
          />
        </div> }

        { (selections.newCustomer || selections.newAccount) && !permanentSelected
        && <div className={ `col-${ inputCols } col-sm-12` }>
          <AsyncSelect
            className={ fieldHasErrors('legalEntities') ? 'is-error' : '' }
            classNamePrefix="ttms"
            isMulti
            closeOnSelect={ false }
            closeMenuOnSelect={ false }
            cacheOptions
            loadOptions={ loadLegalEntities }
            defaultOptions={ legalEntityOptions }
            isDisabled={ (!selections.newCustomer && !selections.newAccount) || !!soleLegalEntity }
            noOptionsMessage={ () => ((legalEntityInputValue && legalEntityOptions && legalEntityOptions.length === 0) ? 'No legal entities found...' : 'Enter a legal entity identifier') }
            value={ legalEntities }
            inputValue={ legalEntityInputValue }
            onInputChange={ onLegalEntityInputChange }
            onChange={ onMultiLegalEntityChange }
          />
        </div> }
        { !selections.newCustomer && !selections.newAccount && legalEntities.length > 1
          && <div className={ `col-${ isHorizonalLayout() ? '8' : '4' } col-sm-12` }>
            <label className="form-label text-left">
              Multiple Legal Entities
            </label>
          </div> }
        { legalEntities.length === 1
          && <React.Fragment>
            { !selections.newCustomer && !selections.newAccount && <div className={ `col-${ isHorizonalLayout() ? '8' : '4' } col-sm-12` }>
              <label className="form-label text-left">
                { legalEntities[0].label }
              </label>
            </div> }
            <div className="col-12 le-property-labels">
              <span>
                { `Chapter 4: ${ legalEntities[0].chapter4Status != null ? legalEntities[0].chapter4Status.toString().padStart(2, '0') : 'NA' }` }
                <HintButton hintType="hint" active={ leChapter4Shown } onClick={ () => { setLeChapter4Shown(!leChapter4Shown); } } />
                { `Chapter 3:  ${ legalEntities[0].chapter3Status != null ? legalEntities[0].chapter3Status.toString().padStart(2, '0') : 'NA' }` }
                <HintButton hintType="hint" active={ leChapter3Shown } onClick={ () => { setLeChapter3Shown(!leChapter3Shown); } } />
                { `CRS: ${ legalEntities[0].crsParticipating ? 'Participating' : 'Non-Participating' }` }
                <span className="ml-1rem"> { `CARF: ${ legalEntities[0].carfParticipating ? 'Participating' : 'Non-Participating' }` }</span>
              </span>
              <div className={ `le-property-desc ${ leChapter3Shown || leChapter4Shown ? '' : 'hide' }` }>
                { leChapter4Shown && <span>{ `Chapter 4: ${ legalEntities[0].chapter4Status.toString().padStart(2, '0') } - ${ legalEntities[0].chapter4StatusDescription }` }</span> }
                { leChapter3Shown && <span>{ `Chapter 3: ${ legalEntities[0].chapter3Status.toString().padStart(2, '0') } - ${ legalEntities[0].chapter3StatusDescription }` }</span> }
              </div>
            </div>
          </React.Fragment> }
      </div>
    </React.Fragment>
  );
}

CustomerAccountInput.propTypes = {
  clientId: PropTypes.number.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  layout: PropTypes.number,
  initialSelections: PropTypes.shape({
    customerId: PropTypes.number,
    customer: PropTypes.shape(),
    newCustomer: PropTypes.bool,
    newAccount: PropTypes.bool,
  }),
  accountExclusions: PropTypes.arrayOf(PropTypes.number),
  accountsPartOfAnImyStructure: PropTypes.arrayOf(PropTypes.shape({
    parentAccountId: PropTypes.number,
    value: PropTypes.number,
    label: PropTypes.string
  })),
  tempCustomerLabel: PropTypes.string,
  permanentCustomer: PropTypes.shape({
    available: PropTypes.bool,
    selected: PropTypes.bool,
    disabled: PropTypes.bool,
    onToggle: PropTypes.func,
    customerId: PropTypes.string,
    customerIdError: PropTypes.string,
    onCustomerIdChange: PropTypes.func,
    legalEntities: PropTypes.arrayOf(PropTypes.shape()),
    onLegalEntityChange: PropTypes.func
  })
};

CustomerAccountInput.defaultProps = {
  layout: CustomerAccountInputLayout.Default,
  initialSelections: null,
  accountExclusions: null,
  accountsPartOfAnImyStructure: null,
  tempCustomerLabel: 'Create New Customer',
  permanentCustomer: null
};

export default CustomerAccountInput;
